import { JSX, useMemo } from "react";

import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { Box, Chip, Theme, useTheme } from "@mui/material";
import { formatNum } from "src/lib/number.helpers";
import { type MeasuresSummarizedItemDto } from "src/types/dashboardDatasetDto";

const getStyles = (theme: Theme, fontSize: string) => ({
  label: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  differencePositive: {
    fontSize,
    backgroundColor: theme.palette.transparent.pure,
    color: theme.palette.positive.text,
  },
  iconPositive: {
    fontSize,
    color: theme.palette.positive.text,
  },
  differenceNegative: {
    fontSize,
    backgroundColor: theme.palette.transparent.pure,
    color: theme.palette.negative.text,
  },
  iconNegative: {
    fontSize,
    color: theme.palette.negative.text,
  },
  default: {
    fontSize,
    backgroundColor: theme.palette.transparent.pure,
  },
});

function Difference({
  row,
  fontSize,
  showInParentheses = false,
}: {
  row: MeasuresSummarizedItemDto;
  fontSize: string;
  showInParentheses?: boolean;
}): JSX.Element | null {
  const theme = useTheme();
  const styles = getStyles(theme, fontSize);
  const currentValue = row.currentValue ?? 0;
  const pastValue = row.pastValue ?? 0;
  const diff = currentValue - pastValue;

  const absoluteDiff = useMemo(() => {
    const formattedDiff = formatNum(Math.abs(diff));
    let icon;
    if (diff > 0) {
      icon = <AddIcon style={styles.iconPositive} />;
    } else if (diff === 0) {
      return "";
    } else {
      icon = <RemoveIcon style={styles.iconNegative} />;
    }

    if (showInParentheses) {
      return (
        <Box sx={styles.label}>
          ({icon}
          {formattedDiff})
        </Box>
      );
    }

    return (
      <Box sx={styles.label}>
        {icon}
        {formattedDiff}
      </Box>
    );
  }, [diff, showInParentheses, styles]);

  if (!absoluteDiff) {
    return null;
  }
  if (diff > 0) {
    return <Chip sx={styles.differencePositive} size='small' label={absoluteDiff} />;
  }
  if (diff < 0) {
    return <Chip sx={styles.differenceNegative} size='small' label={absoluteDiff} />;
  }
  return <Chip size='small' sx={styles.default} label={absoluteDiff} />;
}

export default Difference;
